import React from 'react';
import {
    TextField,
    Typography,
    Link,
    Button,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormLabel,
    FormControl,
    Select,
    Paper,
    MenuItem,
    InputAdornment,
    makeStyles,
    Grid
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { ERoles } from '../../utils/enum';
import { CountryPhoneList } from '../../utils/CountryList';
import { Stack } from "@mui/material";
import { SelectEndornmentNation } from './Register/TextFieldNation';
import { IGuestUser } from '../../utils/ReservationInterfaces';
import { IAnnounce } from '../../utils/AnnounceInterfaces';
import i18n from '../../i18n/config';

const useStyles = makeStyles((theme) => ({
    formLoginContainer: {
        backgroundColor: 'white',
        height: 'auto',
        marginTop: 20,
    },
    sectionHeader: {
        margin: theme.spacing(3, 0, 2),
        fontWeight: 'bold',
    },
    optionalLabel: {
        fontStyle: 'italic',
        color: 'black',
        fontSize: '0.875rem'
    },
    formControl: {
        margin: theme.spacing(2, 0),
    },
    loginButton: {
        width: '50%',
        aligItems: 'center',
        background: '#890608',
        color: 'white',
        borderRadius: 100,
        fontFamily: 'Josefin Sans',
        fontSize: 14,
        textTransform: 'none',
        padding: '8px 14px',
        marginTop: theme.spacing(3),
        '&:hover': {
            background: '#B5551B',
        },
    },
    helperText: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    userInfoSection: {
        marginBottom: theme.spacing(3),
        backgroundColor: 'white',
        borderRadius: theme.shape.borderRadius,
    },
    userInfoItem: {
        marginBottom: theme.spacing(1),
    },
    root: {
        [theme.breakpoints.down("sm")]: {
            marginBottom: 50,
        },
    },
    userInfoPaper: {
        backgroundColor: 'white',
        padding: 24,
        marginBottom: theme.spacing(3),
    },
}));


interface GuestCheckoutFormProps {
    onSuccess: (values: FormValues) => void;
    onLoginClick?: () => void;
    isLoggedIn: boolean;
    user: IGuestUser;
    values?: FormValues;  // Add this
    onChange?: (newValues: FormValues) => void;  // Add this
    showSubmitButton?: boolean; // Add this prop
    onValidationChange?: (isValid: boolean) => void;
    announce: IAnnounce;
}

// If you can modify IGuestData to match FormValues:
interface FormValues {
    // Guest Info
    firstName: string;
    lastName: string;
    email: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
    phone: string;
    whatsappNumber: string;
    useSameNumberForWhatsapp: boolean;
    whatsappCountryCode: string; // Add this
    whatsappPhoneCode: string;   // Add this
    whatsappCountryLogo: string; // Add this
    
    // Booking Preferences
    digitalConfirmation: boolean;
    bookingFor: string;
    travelPurpose: string;
    carRentalInterest: boolean;
    specialRequests: string;
    arrivalTime: string;
}



export const GuestCheckoutForm: React.FC<GuestCheckoutFormProps> = ({ 
    onSuccess,
    isLoggedIn,
    user = {
        firstName: '',
        lastName: '',
        email: '',
        country: '',  // Add default
        phone: '',    // Add default
        role: { name: '' }
    },
    showSubmitButton = true,  // Add default value for optional prop
    onValidationChange,
    onChange,
    announce,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [codephone, setCodePhone] = React.useState<string>("");
    const [values, setValues] = React.useState<FormValues>({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        address: '',
        city: '',
        postalCode: '',
        country: user.country || '', // Provide empty string as fallback
        phone: user.phone || '',     // Provide empty string as fallback
        whatsappNumber: user.phone || '', // Initialize with phone if available
        useSameNumberForWhatsapp: true, // Default checked
        whatsappCountryCode: user.country || '', // Initialize with user's country if available
        whatsappPhoneCode: codephone || '' ,      // Initialize with main phone code
        whatsappCountryLogo: '', // Add this
        digitalConfirmation: true,
        bookingFor: 'myself',
        travelPurpose: 'leisure',
        carRentalInterest: false,
        specialRequests: '',
        arrivalTime: '',
    });


    // Login dialog state
    const [loginDialogOpen, setLoginDialogOpen] = React.useState(false);
    const [openRegister, setOpenRegister] = React.useState(false);
    const [formStateBeforeLogin, setFormStateBeforeLogin] = React.useState<FormValues | null>(null);
    const [logo, setLogoPays] = React.useState<string>("");
    const [isProcessingLogin, setIsProcessingLogin] = React.useState(false);
    const [formErrors, setFormErrors] = React.useState<Record<string, boolean>>({
        firstName: false,
        lastName: false,
        email: false,
        country: false,
        phone: false
      });
    // Add to your state initialization
    const [whatsappCountry, setWhatsappCountry] = React.useState({
        code: user.country || '',
        logo: '',
        phoneCode: codephone || ''
    });
    
    
      const validateFields = React.useCallback((values: FormValues) => {
        const errors = {
          firstName: !values.firstName,
          lastName: !values.lastName,
          email: !values.email,
          country: !values.country,
          phone: !values.phone
        };
        
        setFormErrors(errors);
        onValidationChange?.(!Object.values(errors).some(Boolean));
        
        return !Object.values(errors).some(Boolean);
      }, [onValidationChange]);

    React.useEffect(() => {
        if (isLoggedIn) {
            // When user logs in, merge their data with existing form values
            setValues(prev => ({
                ...prev,
                firstName: user.firstName || prev.firstName,
                lastName: user.lastName || prev.lastName,
                email: user.email || prev.email,
                country: user.country || prev.country,
                phone: user.phone || prev.phone
            }));
            
            // Clear any preserved guest data
            setFormStateBeforeLogin(null);
        }
    }, [isLoggedIn, user]);

    React.useEffect(() => {
        if (values.useSameNumberForWhatsapp) {
            const newValues = {
                ...values,
                whatsappNumber: values.phone
            };
            setValues(newValues);
            onChange?.(newValues);
        }
    }, [values.phone]);

    // Open login dialog and preserve form state
    const handleOpenLogin = (e: React.MouseEvent) => {
        e.preventDefault();
        setLoginDialogOpen(true);
    };

    // Close login dialog
    const handleCloseLogin = () => {
        setLoginDialogOpen(false);
    };

    // Handle successful login
    const handleLoginSuccess = () => {
        setLoginDialogOpen(false);
        setIsProcessingLogin(true);
        // No need to restore guest data here - useEffect will handle it
    };

    // Register dialog handlers
    const handleOpenRegister = () => {
        setOpenRegister(true);
        setLoginDialogOpen(false);
    };

    const handleCloseRegister = () => {
        setOpenRegister(false);
    };

    // Add these handler functions
    const handleWhatsappCountryChange = (nation: { code: string; logo: string; phoneCode: string }) => {
        setWhatsappCountry({
            code: nation.code,
            logo: nation.logo,
            phoneCode: nation.phoneCode
        });
        
        const newValues = {
            ...values,
            whatsappCountryCode: nation.code,
            whatsappPhoneCode: nation.phoneCode,
            whatsappCountryLogo: nation.logo // Add this
        };
        setValues(newValues);
        onChange?.(newValues);
    };
   

    const handleChange = (prop: keyof FormValues) => (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      ) => {
        const newValues = {
          ...values,
          [prop]: event.target.value
        };
        
        setValues(newValues);
        onChange?.(newValues);
        
        validateFields(newValues);
      };

    const handleCheckboxChange = (prop: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setValues({ ...values, [prop]: event.target.checked });
    };

    const handleOnChangeSelect = (key: string, v: string) => {
        setValues({ ...values, [key]: v });
    };

    const handleSelectChange = (prop: keyof FormValues) => (
        event: React.ChangeEvent<{ name?: string; value: unknown }>
      ) => {
        const newValues = {
          ...values,
          [prop]: event.target.value
        };
        
        setValues(newValues);
        onChange?.(newValues);
        
        validateFields(newValues);
      };

      const handleOnChangeNation = (val: any) => {
        setCodePhone(val.dial_code);
        setLogoPays(val.code);
        
        const newValues = {
            ...values,
            country: val.code,
            // Update WhatsApp country if using same number
          
        };
        
        setValues(newValues);
        onChange?.(newValues);
        
        
    };

    const validateForm = () => {
        return !Object.values(formErrors).some(Boolean);
      };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (validateForm() && onSuccess) {
            onSuccess(values);
        }
    };

    const handleWhatsappCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const newValues = {
            ...values,
            useSameNumberForWhatsapp: isChecked,
            whatsappNumber: isChecked ? values.phone : values.whatsappNumber,
            // Only sync country/code when checking the box
            ...(isChecked ? {
                whatsappCountryCode: values.country,
                whatsappPhoneCode: codephone,
                whatsappCountryLogo: logo
            } : {})
        };
        
        setValues(newValues);
        onChange?.(newValues);
        
        // Update the whatsappCountry state if needed
        if (isChecked) {
            setWhatsappCountry({
                code: values.country,
                logo: logo,
                phoneCode: codephone
            });
        }
    };

    const handleWhatsappNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValues = {
            ...values,
            whatsappNumber: event.target.value,
            useSameNumberForWhatsapp: event.target.value === values.phone
        };
        
        setValues(newValues);
        onChange?.(newValues);
    };

    

    const needsPhoneInfo = isLoggedIn && (!user.phone || !user.country);
    return (
        <div className={classes.root}>
            <Paper elevation={2} className={classes.userInfoPaper}>
            <form onSubmit={handleSubmit} className={classes.formLoginContainer}>
                {!isLoggedIn && (
                    <>
                        {(!values.firstName || !values.lastName || !values.email || !values.country || !values.phone) && (
                        <Typography color="error" variant="body2" style={{ marginBottom: 10 }}>
                            {t('error.pleaseCompleteAllRequiredFields')}
                        </Typography>
                        )}
                        <Typography variant="body1" gutterBottom>
                            {t('guestCheckout.loginPrompt')}
                            <Link href="#" onClick={handleOpenLogin} style={{ color: '#890608', textDecoration: 'underline' }}>
                                {t('guestCheckout.loginLink')}
                            </Link>
                            {t('guestCheckout.orText')}
                            <Link href="#" onClick={handleOpenRegister} style={{ color: '#890608', textDecoration: 'underline' }}>
                                {t('guestCheckout.registerLink')}
                            </Link>
                            {t('guestCheckout.endText')}
                        </Typography>
                    </>
                )}

                {isLoggedIn ? (
                    <>
                        {/* Display user information section */}
                        <div className={classes.userInfoSection}>
                            <Typography style={{ marginTop:0, marginBottom: 16 , fontSize: 20, fontWeight: 'bold'}} >
                                {t('guestCheckout.yourInformation')}
                            </Typography>

                            <div className={classes.userInfoItem}>
                                <Typography><strong>{t('guestCheckout.firstName')}:</strong> {user.firstName}</Typography>
                            </div>
                            <div className={classes.userInfoItem}>
                                <Typography><strong>{t('guestCheckout.lastName')}:</strong> {user.lastName}</Typography>
                            </div>
                            <div className={classes.userInfoItem}>
                                <Typography><strong>{t('guestCheckout.email')}:</strong> {user.email}</Typography>
                            </div>
                            {user.country && (
                            <div className={classes.userInfoItem}>
                                <Typography><strong>{t('guestCheckout.country')}:</strong> {user.country}</Typography>
                            </div>
                            )}
                            {user.phone && (
                                <div className={classes.userInfoItem}>
                                    <Typography><strong>{t('guestCheckout.phone')}:</strong> {user.phone}</Typography>
                                </div>
                            )}

                            {/* Show country/phone form if missing */}
                            {needsPhoneInfo && (
                                <>
                                    {/* Add validation message */}
                                    {(!values.country || !values.phone) && (
                                        <Typography color="error" variant="body2" style={{ marginTop: 10, marginBottom: 10 }}>
                                            {t('error.pleaseProvideCountryAndPhone')}
                                        </Typography>
                                    )}

                                    <Grid container spacing={1}>
                                        <Grid item lg={4} md={4} sm={4} xs={4}>
                                            <SelectEndornmentNation
                                                id="country"
                                                name="country"
                                                values={CountryPhoneList}
                                                IconElement={
                                                    logo ? (
                                                        <img
                                                            style={{ width: 32 }}
                                                            src={`${logo}`}
                                                            alt={"Flag"}
                                                        />
                                                    ) : (
                                                        ""
                                                    )
                                                }
                                                selectedValue=""
                                                handleOnChange={handleOnChangeSelect}
                                                handleOnChangeNation={handleOnChangeNation}
                                                error={formErrors.country}
                                                helperText={formErrors.country ? t('error.fieldRequired') : ''}
                                            />
                                        </Grid>
                                        <Grid item lg={8} md={8} sm={8} xs={8}>
                                            <Stack direction="row" alignItems="center" display="flex">
                                                <TextField
                                                    margin="dense"
                                                    id="phone"
                                                    name="phone"  // Changed from "numberPhone"
                                                    variant="outlined"
                                                    onChange={(e) => handleChange('phone')(e)}
                                                    value={values.phone || user.phone || ''}
                                                    type="text"
                                                    fullWidth
                                                    label={`${t("SignUp.phone")}*`}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <div style={{ marginRight: 10 }}>
                                                                {codephone}
                                                            </div>
                                                        ),
                                                    }}
                                                    required
                                                    error={formErrors.phone}
                                                    helperText={formErrors.phone ? t('error.fieldRequired') : ''}
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {/* WhatsApp Number Section */}
                <Grid container spacing={1} style={{ marginTop: 8 }}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={values.useSameNumberForWhatsapp}
                                    onChange={handleWhatsappCheckboxChange}
                                    color="primary"
                                />
                            }
                            label={t('guestCheckout.sameNumberForWhatsapp')}
                        />
                    </Grid>
                    {!values.useSameNumberForWhatsapp && (
                        <Grid item xs={12}>
                            <Grid container spacing={1}>
                                <Grid item lg={4} md={4} sm={4} xs={4}>
                                    <SelectEndornmentNation
                                        id="whatsappCountry"
                                        name="whatsappCountry"
                                        values={CountryPhoneList}
                                        IconElement={
                                            whatsappCountry.logo ? (
                                                <img
                                                    style={{ width: 32 }}
                                                    src={`${whatsappCountry.logo}`}
                                                    alt={"Flag"}
                                                />
                                            ) : (
                                                ""
                                            )
                                        }
                                        selectedValue={values.whatsappCountryCode}
                                        handleOnChange={handleOnChangeSelect}
                                        handleOnChangeNation={handleWhatsappCountryChange}
                                    />
                                </Grid>
                                <Grid item lg={8} md={8} sm={8} xs={8}>
                                    <Stack direction="row" alignItems="center" display="flex">
                                        <TextField
                                            margin="dense"
                                            id="whatsappNumber"
                                            name="whatsappNumber"
                                            variant="outlined"
                                            onChange={handleWhatsappNumberChange}
                                            value={values.whatsappNumber}
                                            type="text"
                                            fullWidth
                                            label={t('guestCheckout.whatsappNumber')}
                                            InputProps={{
                                                startAdornment: (
                                                    <div style={{ marginRight: 10 }}>
                                                        {values.whatsappPhoneCode || whatsappCountry.phoneCode || codephone}
                                                    </div>
                                                ),
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                            </Grid>
                            <Typography variant="body2" className={classes.helperText}>
                                {t('guestCheckout.whatsappHelperText')}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                        </div>
                    </>
                ) : (
                    <>

                        {/* Personal details section */}
                        <Typography variant="h6" className={classes.sectionHeader}>
                            {t('guestCheckout.personalDetails')}
                        </Typography>
                        
                        <TextField
                            label={`${t('guestCheckout.firstName')}*`}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={values.firstName}
                            onChange={handleChange('firstName')}
                            required
                            error={formErrors.firstName}
                            helperText={formErrors.firstName ? t('error.fieldRequired') : '' }
                        />
                        
                        <TextField
                            label={`${t('guestCheckout.lastName')}*`}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={values.lastName}
                            onChange={handleChange('lastName')}
                            required
                            error={formErrors.lastName}
                            helperText={formErrors.lastName ? t('error.fieldRequired') : '' }
                        />
                        
                        <TextField
                            label={`${t('guestCheckout.email')}*`}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            type="email"
                            value={values.email}
                            onChange={handleChange('email')}
                            required
                            error={formErrors.email}
                            helperText={formErrors.email ? t('error.fieldRequired') : '' }
                        />

                        {/* Contact information section */}
                        <Typography variant="h6" className={classes.sectionHeader}>
                            {t('guestCheckout.contactInfo')}
                        </Typography>
                        
                        <TextField
                            label={`${t('guestCheckout.address')}*`}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={values.address}
                            onChange={handleChange('address')}
                            required
                        />
                        
                        <TextField
                            label={`${t('guestCheckout.city')}*`}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={values.city}
                            onChange={handleChange('city')}
                            required
                        />
                        
                        <TextField
                            label={`${t('guestCheckout.postalCode')} (${t('guestCheckout.optional')})`}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={values.postalCode}
                            onChange={handleChange('postalCode')}
                        />
                        
                         {/* Country and Phone Input - using the enhanced component */}
                        <Grid container spacing={1}>
                            <Grid item lg={4} md={4} sm={4} xs={4}>
                                <SelectEndornmentNation
                                    id="country"
                                    name="country"
                                    values={CountryPhoneList}
                                    IconElement={
                                        logo ? (
                                            <img
                                                style={{ width: 32 }}
                                                src={`${logo}`}
                                                alt={"Flag"}
                                            />
                                        ) : (
                                            ""
                                        )
                                    }
                                    selectedValue=""
                                    handleOnChange={handleOnChangeSelect}
                                    handleOnChangeNation={handleOnChangeNation}
                                    error={formErrors.firstName}
                                    helperText={formErrors.firstName ? t('error.fieldRequired') : ''}
                                />
                            </Grid>
                            <Grid item lg={8} md={8} sm={8} xs={8}>
                                <Stack direction="row" alignItems="center" display="flex">
                                <TextField
                                    margin="dense"
                                    id="phone"
                                    name="phone"  // Changed from "numberPhone"
                                    variant="outlined"
                                    onChange={(e) => handleChange('phone')(e)}
                                    value={values.phone}
                                    type="text"
                                    fullWidth
                                    label={`${t("SignUp.phone")}*`}
                                    InputProps={{
                                        startAdornment: (
                                            <div style={{ marginRight: 10 }}>
                                                {codephone}
                                            </div>
                                        ),
                                    }}
                                    required
                                    error={formErrors.phone}
                                    helperText={formErrors.phone ? t('error.fieldRequired') : ''}
                                />
                                </Stack>
                            </Grid>
                        </Grid>
                        {/* WhatsApp Number Section */}
                        <Grid container spacing={1} style={{ marginTop: 8 }}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={values.useSameNumberForWhatsapp}
                                            onChange={handleWhatsappCheckboxChange}
                                            color="primary"
                                        />
                                    }
                                    label={t('guestCheckout.sameNumberForWhatsapp')}
                                />
                            </Grid>
                            {!values.useSameNumberForWhatsapp && (
                            <Grid item xs={12}>
                            <Grid container spacing={1}>
                                <Grid item lg={4} md={4} sm={4} xs={4}>
                                <SelectEndornmentNation
                                    id="whatsappCountry"
                                    name="whatsappCountry"
                                    values={CountryPhoneList}
                                    IconElement={
                                    whatsappCountry?.logo ? (
                                        <img
                                        style={{ width: 32 }}
                                        src={whatsappCountry.logo}
                                        alt="Flag"
                                        />
                                    ) : null
                                    }
                                    selectedValue={values.whatsappCountryCode || whatsappCountry?.code}
                                    handleOnChange={handleOnChangeSelect}
                                    handleOnChangeNation={handleWhatsappCountryChange}
                                />
                                </Grid>
                                <Grid item lg={8} md={8} sm={8} xs={8}>
                                <Stack direction="row" alignItems="center" display="flex">
                                    <TextField
                                    margin="dense"
                                    id="whatsappNumber"
                                    name="whatsappNumber"
                                    variant="outlined"
                                    onChange={handleWhatsappNumberChange}
                                    value={values.whatsappNumber}
                                    type="text"
                                    fullWidth
                                    label={t('guestCheckout.whatsappNumber')}
                                    InputProps={{
                                        startAdornment: (
                                        <div style={{ marginRight: 10 }}>
                                            {values.whatsappPhoneCode || whatsappCountry?.phoneCode || codephone}
                                        </div>
                                        ),
                                    }}
                                    />
                                </Stack>
                                </Grid>
                            </Grid>
                            <Typography variant="body2" className={classes.helperText}>
                                {t('guestCheckout.whatsappHelperText')}
                            </Typography>
                            </Grid>
                        )}
                        </Grid>
                    </>
                )}    

                    {(user.role?.name === ERoles.SUPER_ADMIN ||
                    user.role?.name === ERoles.COUNTRY_ADMIN ||
                    user.role?.name === ERoles.ADMIN_SUPPORT) && (
                        <Grid style={{ marginTop: 15}}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend" style={{ fontWeight: 'bold', color: 'black'}}>
                                {t('guestCheckout.bookingFor')} 
                            </FormLabel>
                            <RadioGroup
                                value={values.bookingFor}
                                onChange={handleChange('bookingFor')}
                            >
                                <FormControlLabel
                                    value="myself"
                                    control={<Radio />}
                                    label={t('guestCheckout.mainGuest')}
                                />
                                <FormControlLabel
                                    value="someoneElse"
                                    control={<Radio />}
                                    label={t('guestCheckout.someoneElse')}
                                />
                            </RadioGroup>
                            {values.bookingFor === 'someoneElse' && (
                                <TextField
                                    label={t('guestCheckout.guestDetails')}
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                />
                            )}
                        </FormControl>
                    </Grid>    

                        )}

                        {/* Digital confirmation checkbox */}
                        {/*
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={values.digitalConfirmation}
                                    onChange={handleCheckboxChange('digitalConfirmation')}
                                    color="primary"
                                />
                            }
                            label={
                                <>
                                    {t('guestCheckout.digitalConfirmation')}
                                    <Typography variant="body2" className={classes.helperText}>
                                        {t('guestCheckout.digitalConfirmationHelper')}
                                    </Typography>
                                </>
                            }
                        /> */}
                        

                        {/* Travel purpose section */}
                        <Grid style={{ marginTop: 15}}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend" style={{ fontWeight: 'bold', color: 'black'}}>
                                {t('guestCheckout.travelPurpose')} 
                            </FormLabel>
                            <RadioGroup
                                value={values.travelPurpose}
                                onChange={handleChange('travelPurpose')}
                            >
                                <FormControlLabel
                                    value="business"
                                    control={<Radio />}
                                    label={t('guestCheckout.business')}
                                />
                                <FormControlLabel
                                    value="leisure"
                                    control={<Radio />}
                                    label={t('guestCheckout.leisure')}
                                />
                            </RadioGroup>
                        </FormControl>
                        </Grid>

                        {/* Car rental offer */}
                        {/*
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={values.carRentalInterest}
                                    onChange={handleCheckboxChange('carRentalInterest')}
                                    color="primary"
                                />
                            }
                            label={
                                <>
                                    {t('guestCheckout.carRental')}
                                    <Typography variant="body2" className={classes.helperText}>
                                        {t('guestCheckout.carRentalHelper')}
                                    </Typography>
                                </>
                            }
                        /> */}

                        {/* Special requests */}
                        {/*
                        <TextField
                            label={
                                <>
                                    {t('guestCheckout.specialRequests')}
                                    <span className={classes.optionalLabel}> ({t('guestCheckout.optional')})</span>
                                </>
                            }
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            multiline
                            rows={4}
                            value={values.specialRequests}
                            onChange={handleChange('specialRequests')}
                            helperText={t('guestCheckout.specialRequestsHelper')}
                        />  */}

                        <Grid xs={8} sm={8} md={8} lg={6} xl={6}>
                        {/* Arrival time */}
                        <FormControl fullWidth variant="outlined" margin="normal">
                            <FormLabel component="legend">
                                <p style={{ fontWeight: 'bold', color: 'black'}}>
                                {t('guestCheckout.arrivalTime')}
                                </p>
                                <span className={classes.optionalLabel}>
                                    {t('guestCheckout.addArrivalTime')} ({t('guestCheckout.optional')})
                                </span>
                            </FormLabel>
                            <Select
                                value={values.arrivalTime}
                                onChange={handleSelectChange('arrivalTime')}
                                displayEmpty
                                variant="outlined"
                            >
                                <MenuItem value="">{t('guestCheckout.selectTime')}</MenuItem>
                                <MenuItem value="Je ne sais pas">{t('guestCheckout.je-ne-sais-pas')}</MenuItem>
                                <MenuItem value="0h00 - 1h00">0h00 - 1h00</MenuItem>
                                <MenuItem value="1h00 - 2h00">1h00 - 2h00</MenuItem>
                                <MenuItem value="2h00 - 3h00">2h00 - 3h00</MenuItem>
                                <MenuItem value="3h00 - 4h00">3h00 - 4h00</MenuItem>
                                <MenuItem value="4h00 - 5h00">4h00 - 5h00</MenuItem>
                                <MenuItem value="5h00 - 6h00">5h00 - 6h00</MenuItem>
                                <MenuItem value="6h00 - 7h00">6h00 - 7h00</MenuItem>
                                <MenuItem value="7h00 - 8h00">7h00 - 8h00</MenuItem>
                                <MenuItem value="8h00 - 9h00">8h00 - 9h00</MenuItem>
                                <MenuItem value="9h00 - 10h00">9h00 - 10h00</MenuItem>
                                <MenuItem value="10h00 - 11h00">10h00 - 11h00</MenuItem>
                                <MenuItem value="11h00 - 12h00">11h00 - 12h00</MenuItem>
                                <MenuItem value="12h00 - 13h00">12h00 - 13h00</MenuItem>
                                <MenuItem value="13h00 - 14h00">13h00 - 14h00</MenuItem>
                                <MenuItem value="14h00 - 15h00">14h00 - 15h00</MenuItem>
                                <MenuItem value="15h00 - 16h00">15h00 - 16h00</MenuItem>
                                <MenuItem value="16h00 - 17h00">16h00 - 17h00</MenuItem>
                                <MenuItem value="17h00 - 18h00">17h00 - 18h00</MenuItem>
                                <MenuItem value="18h00 - 19h00">18h00 - 19h00</MenuItem>
                                <MenuItem value="19h00 - 20h00">19h00 - 20h00</MenuItem>
                                <MenuItem value="20h00 - 21h00">20h00 - 21h00</MenuItem>
                                <MenuItem value="21h00 - 22h00">21h00 - 22h00</MenuItem>
                                <MenuItem value="22h00 - 23h00">22h00 - 23h00</MenuItem>
                                <MenuItem value="23h00 - 00h00">23h00 - 00h00</MenuItem>
                                <MenuItem value="0h00 - 1h00">{t('guestCheckout.1h-le-lendemain')}</MenuItem>
                                <MenuItem value="1h00 - 2h00">{t('guestCheckout.2h-le-lendemain')}</MenuItem>                                
                            </Select>
                            <Typography variant="caption" className={classes.helperText}>
                                {t('guestCheckout.timezoneNote', {
                                    city: i18n.language === 'fr'
                                    ? announce.property?.address?.city?.nomFR
                                    : announce.property?.address?.city?.nomEN
                                })}
                            </Typography>
                        </FormControl>
                        </Grid>

                        {/* Submit button */}
                        <div style={{ 
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: 24,
                            width: '100%'
                        }}>
                            {showSubmitButton && (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    className={classes.loginButton}
                                    disabled={!validateForm()}
                                >
                                    {t('guestCheckout.continueToPayment')}
                                </Button>
                            )}
                        </div>
                

                        
            </form>
            </Paper>
        </div>
    );
};